/**
 * Firebase initialization + FCM token management.
 *
 * Firebase client config is fetched from the backend at runtime
 * so SuperAdmin can update it without a frontend redeploy.
 *
 * Usage:
 *   import { requestFCMToken, onForegroundMessage } from '@/lib/firebase';
 */
import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import { api } from '@/lib/api';

let _app = null;
let _messaging = null;
let _config = null;

/** Fetch FCM client config from backend (cached). */
async function getFCMConfig() {
  if (_config) return _config;
  try {
    const { data } = await api.get('/notifications/fcm-config');
    if (data?.api_key) {
      _config = data;
      return data;
    }
  } catch {
    // FCM not configured by SuperAdmin
  }
  return null;
}

/** Initialize Firebase app (idempotent). */
async function getFirebaseApp() {
  if (_app) return _app;
  const cfg = await getFCMConfig();
  if (!cfg) return null;

  const firebaseConfig = {
    apiKey:            cfg.api_key,
    authDomain:        cfg.auth_domain,
    projectId:         cfg.project_id,
    storageBucket:     cfg.storage_bucket,
    messagingSenderId: cfg.messaging_sender_id,
    appId:             cfg.app_id,
  };

  _app = getApps().length === 0
    ? initializeApp(firebaseConfig)
    : getApps()[0];
  return _app;
}

/** Initialize FCM messaging instance. */
async function getFirebaseMessaging() {
  if (_messaging) return _messaging;
  const supported = await isSupported().catch(() => false);
  if (!supported) return null;

  const app = await getFirebaseApp();
  if (!app) return null;

  _messaging = getMessaging(app);

  // Register service worker and send config to it
  if ('serviceWorker' in navigator) {
    const reg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    const cfg = await getFCMConfig();
    if (cfg && reg.active) {
      reg.active.postMessage({
        type: 'FIREBASE_CONFIG',
        config: {
          apiKey: cfg.api_key,
          authDomain: cfg.auth_domain,
          projectId: cfg.project_id,
          storageBucket: cfg.storage_bucket,
          messagingSenderId: cfg.messaging_sender_id,
          appId: cfg.app_id,
        },
      });
    }
  }

  return _messaging;
}

/**
 * Request notification permission, get FCM token, register with backend.
 * Returns { ok, token, reason }
 */
export async function requestFCMToken() {
  try {
    const cfg = await getFCMConfig();
    if (!cfg) return { ok: false, reason: 'FCM not configured by administrator' };
    if (!cfg.vapid_key) return { ok: false, reason: 'VAPID key not configured' };

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      return { ok: false, reason: 'Notification permission denied' };
    }

    const messaging = await getFirebaseMessaging();
    if (!messaging) return { ok: false, reason: 'FCM not supported in this browser' };

    const token = await getToken(messaging, { vapidKey: cfg.vapid_key });
    if (!token) return { ok: false, reason: 'Failed to get FCM token' };

    // Register token with backend
    await api.post('/notifications/fcm-token', { token });
    return { ok: true, token };
  } catch (err) {
    return { ok: false, reason: err.message };
  }
}

/**
 * Listen for foreground messages (app is open).
 * Callback receives the payload: { notification: {title, body}, data: {} }
 */
export async function onForegroundMessage(callback) {
  const messaging = await getFirebaseMessaging();
  if (!messaging) return () => {};
  return onMessage(messaging, callback);
}

export { getFCMConfig };
